import {
	ArrowLeftOutlined,
	DeleteOutlined,
	DesktopOutlined,
	MobileOutlined,
	ReloadOutlined,
	SaveOutlined,
	SearchOutlined,
	WarningOutlined,
} from '@ant-design/icons'
import {
	Alert,
	Button,
	Input,
	Popconfirm,
	Space,
	Spin,
	Table,
	Tabs,
	Tag,
	Tooltip,
	message,
} from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import api from '../../api/index.api'
import webEnBundle from '../../i18n/webapp-en.json'
import mobileEnBundle from '../../i18n/mobile-en.json'
import { ROUTE } from '../../utils/constant'

// Mobile keys are stored in DB with the "mob." prefix so they never collide
// with webapp keys. The admin editor adds/strips this prefix transparently.
const MOB_PREFIX = 'mob.'

const PAGE_SIZE = 100

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Flatten a potentially nested JSON object into dotted paths.
 * e.g. { common: { cancel: "Cancel" } } → { "common.cancel": "Cancel" }
 */
function flattenBundle(obj, parentKey = '', result = {}) {
	for (const [k, v] of Object.entries(obj)) {
		const fullKey = parentKey ? `${parentKey}.${k}` : k
		if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
			flattenBundle(v, fullKey, result)
		} else {
			result[fullKey] = typeof v === 'string' ? v : JSON.stringify(v)
		}
	}
	return result
}

// Pre-flatten once at module load — these never change at runtime.
const FLAT_WEB_EN = flattenBundle(webEnBundle)
const FLAT_MOB_EN = flattenBundle(mobileEnBundle)

/**
 * Build table rows by merging a flat bundle (baseline) with DB overrides.
 * For mobile rows the DB key has "mob." prefix — we strip it for display
 * but keep the full dbKey for save/delete operations.
 */
function buildRows(flatBundle, dbOverrides, platform) {
	const overrideMap = new Map(dbOverrides.map((o) => [o.key, o.value]))
	return Object.entries(flatBundle).map(([bundleKey, enValue]) => {
		const dbKey = platform === 'mobile' ? `${MOB_PREFIX}${bundleKey}` : bundleKey
		return {
			key: bundleKey,  // display key — no mob. prefix
			dbKey,            // actual DB key — mob. prefix for mobile
			enValue,
			dbValue: overrideMap.get(dbKey) ?? '',
			dirty: null,
		}
	})
}

// ---------------------------------------------------------------------------
// Inner editor — shared by both tabs
// ---------------------------------------------------------------------------

const TranslationTable = ({ platform, langCode, langInfo, flatBundle }) => {
	const [rows, setRows] = useState([])
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [query, setQuery] = useState('')
	const [dirtyCount, setDirtyCount] = useState(0)
	const mountedRef = useRef(true)

	useEffect(() => {
		mountedRef.current = true
		return () => { mountedRef.current = false }
	}, [])

	const load = useCallback(async () => {
		if (!Object.keys(flatBundle).length) return
		setLoading(true)
		try {
			const rs = await api.i18nTranslationApi.list(langCode, { platform, pageSize: 9999 })
			const overrides = Array.isArray(rs?.data) ? rs.data : []
			if (!mountedRef.current) return
			setRows(buildRows(flatBundle, overrides, platform))
			setDirtyCount(0)
		} catch (err) {
			message.error(err?.message || 'Could not load translations.')
		} finally {
			if (mountedRef.current) setLoading(false)
		}
	}, [langCode, platform, flatBundle])

	useEffect(() => { load() }, [load])

	const handleCellEdit = useCallback((bundleKey, value) => {
		setRows((prev) => {
			let delta = 0
			const next = prev.map((r) => {
				if (r.key !== bundleKey) return r
				const wasDirty = r.dirty !== null
				const isDirty = value !== r.dbValue
				if (!wasDirty && isDirty) delta++
				if (wasDirty && !isDirty) delta--
				return { ...r, dirty: isDirty ? value : null }
			})
			setDirtyCount((c) => c + delta)
			return next
		})
	}, [])

	const handleSaveAll = async () => {
		const dirtyRows = rows.filter((r) => r.dirty !== null)
		if (!dirtyRows.length) return
		setSaving(true)
		try {
			const entries = dirtyRows.map((r) => ({ key: r.dbKey, value: r.dirty }))
			await api.i18nTranslationApi.upsertBulk(langCode, entries)
			message.success(`Saved ${entries.length} override(s).`)
			setRows((prev) =>
				prev.map((r) =>
					r.dirty !== null ? { ...r, dbValue: r.dirty, dirty: null } : r
				)
			)
			setDirtyCount(0)
		} catch (err) {
			message.error(err?.message || 'Save failed.')
		} finally {
			setSaving(false)
		}
	}

	const handleDeleteOne = async (row) => {
		try {
			await api.i18nTranslationApi.deleteOne(langCode, row.dbKey)
			setRows((prev) =>
				prev.map((r) => (r.key === row.key ? { ...r, dbValue: '', dirty: null } : r))
			)
			if (row.dirty !== null) setDirtyCount((c) => c - 1)
			message.success('Override cleared.')
		} catch (err) {
			message.error(err?.message || 'Delete failed.')
		}
	}

	const handleResetPlatform = async () => {
		try {
			await api.i18nTranslationApi.reset(langCode, platform)
			setRows((prev) => prev.map((r) => ({ ...r, dbValue: '', dirty: null })))
			setDirtyCount(0)
			message.success(`All ${platform} overrides cleared.`)
		} catch (err) {
			message.error(err?.message || 'Reset failed.')
		}
	}

	const filteredRows = useMemo(() => {
		if (!query.trim()) return rows
		const q = query.toLowerCase()
		return rows.filter(
			(r) =>
				r.key.toLowerCase().includes(q) ||
				r.enValue.toLowerCase().includes(q) ||
				(r.dbValue || '').toLowerCase().includes(q) ||
				(r.dirty || '').toLowerCase().includes(q)
		)
	}, [rows, query])

	const overriddenCount = useMemo(() => rows.filter((r) => r.dbValue).length, [rows])

	const isRtl = langInfo?.isRtl

	const columns = [
		{
			title: 'Key',
			dataIndex: 'key',
			width: '28%',
			ellipsis: true,
			render: (key) => (
				<Tooltip title={key}>
					<code style={{ fontSize: 11 }}>{key}</code>
				</Tooltip>
			),
		},
		{
			title: 'English (default)',
			dataIndex: 'enValue',
			width: '28%',
			ellipsis: true,
			render: (v) => <span style={{ color: '#888', fontSize: 12 }}>{v}</span>,
		},
		{
			title: `${langCode?.toUpperCase()} override`,
			width: '36%',
			render: (_, record) => {
				const currentVal = record.dirty !== null ? record.dirty : record.dbValue
				return (
					<Input
						size="small"
						value={currentVal}
						placeholder={record.enValue}
						style={isRtl ? { direction: 'rtl' } : undefined}
						onChange={(e) => handleCellEdit(record.key, e.target.value)}
						suffix={
							record.dirty !== null ? (
								<Tag color="warning" style={{ margin: 0, fontSize: 10 }}>
									unsaved
								</Tag>
							) : record.dbValue ? (
								<Tag color="success" style={{ margin: 0, fontSize: 10 }}>
									saved
								</Tag>
							) : null
						}
					/>
				)
			},
		},
		{
			title: '',
			width: 50,
			align: 'center',
			render: (_, record) =>
				record.dbValue ? (
					<Tooltip title="Clear override">
						<Popconfirm
							title="Remove this override?"
							onConfirm={() => handleDeleteOne(record)}
						>
							<Button size="small" danger icon={<DeleteOutlined />} />
						</Popconfirm>
					</Tooltip>
				) : null,
		},
	]

	return (
		<div>
			<div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
				<Input
					prefix={<SearchOutlined />}
					placeholder="Filter by key or value…"
					allowClear
					style={{ width: 280 }}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
				/>
				<Tag>{overriddenCount} overridden</Tag>
				{dirtyCount > 0 && (
					<Tag color="warning" icon={<WarningOutlined />}>
						{dirtyCount} unsaved
					</Tag>
				)}
				<div style={{ flex: 1 }} />
				<Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
					Reload
				</Button>
				<Popconfirm
					title={`Reset all ${platform} overrides for "${langCode}"?`}
					onConfirm={handleResetPlatform}
					okText="Reset"
					okButtonProps={{ danger: true }}
				>
					<Button danger>Reset {platform}</Button>
				</Popconfirm>
				<Button
					type="primary"
					icon={<SaveOutlined />}
					onClick={handleSaveAll}
					loading={saving}
					disabled={dirtyCount === 0}
				>
					Save {dirtyCount > 0 ? `(${dirtyCount})` : ''}
				</Button>
			</div>

			{dirtyCount > 0 && (
				<Alert
					type="warning"
					showIcon
					message={`${dirtyCount} unsaved change(s) — click Save to publish.`}
					style={{ marginBottom: 10 }}
				/>
			)}

			<Spin spinning={loading}>
				<Table
					rowKey="key"
					columns={columns}
					dataSource={filteredRows}
					pagination={{
						defaultPageSize: PAGE_SIZE,
						showSizeChanger: true,
						pageSizeOptions: ['50', '100', '200'],
						showTotal: (t) => `${t} keys`,
					}}
					size="small"
					bordered
					scroll={{ y: 'calc(100vh - 380px)' }}
				/>
			</Spin>
		</div>
	)
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const I18nTranslationEditor = () => {
	const { code } = useParams()
	const history = useHistory()
	const [langInfo, setLangInfo] = useState(null)
	const [loadingLang, setLoadingLang] = useState(true)

	useEffect(() => {
		if (!code) return
		setLoadingLang(true)
		api.i18nLanguageApi
			.getOne(code)
			.then((rs) => setLangInfo(rs?.data ?? rs))
			.catch(() => null)
			.finally(() => setLoadingLang(false))
	}, [code])

	const langTitle = langInfo
		? `${langInfo.name}${langInfo.nativeName && langInfo.nativeName !== langInfo.name ? ` / ${langInfo.nativeName}` : ''}`
		: code

	return (
		<div style={{ padding: 24 }}>
			<Space style={{ marginBottom: 16 }}>
				<Button
					icon={<ArrowLeftOutlined />}
					onClick={() => history.push(`${ROUTE.I18N_MANAGEMENT}${ROUTE.I18N_LANGUAGES}`)}
				>
					Languages
				</Button>
				<Spin spinning={loadingLang} size="small" />
				<span style={{ fontSize: 18, fontWeight: 600 }}>
					UI Translations — <Tag color="blue">{code}</Tag>
					<span style={{ fontSize: 14, fontWeight: 400, marginLeft: 4 }}>{langTitle}</span>
				</span>
			</Space>

			<Alert
				type="info"
				showIcon
				style={{ marginBottom: 16 }}
				message={
					<span>
						<strong>Web strings</strong> are served to the webapp.{' '}
						<strong>Mobile strings</strong> are stored with a{' '}
						<code>mob.</code> prefix in the database and served to the mobile app
						with that prefix stripped. Changes take effect on next app fetch (within cache TTL).
					</span>
				}
			/>

			<Tabs
				defaultActiveKey="web"
				items={[
					{
						key: 'web',
						label: <span><DesktopOutlined /> Webapp strings</span>,
						children: (
							<TranslationTable
								platform="web"
								langCode={code}
								langInfo={langInfo}
								flatBundle={FLAT_WEB_EN}
							/>
						),
					},
					{
						key: 'mobile',
						label: (
							<span>
								<MobileOutlined /> Mobile strings{' '}
								<Tag style={{ marginLeft: 4, fontSize: 10 }} color="purple">mob.</Tag>
							</span>
						),
						children: (
							<TranslationTable
								platform="mobile"
								langCode={code}
								langInfo={langInfo}
								flatBundle={FLAT_MOB_EN}
							/>
						),
					},
				]}
			/>
		</div>
	)
}
